import { useState } from "react"
import { Business, Contact, Household, MinimalUser, User } from "@/schema"

import { MentionUpdateData } from "./use-task-mentions"

interface UseMentionUpdatesProps {
  defaultHousehold?: Household | null
  defaultContact?: Contact | null
  defaultBusiness?: Business | null
  defaultAssignee?: User | null
  defaultCollaborators?: MinimalUser[]
  onRemovePersonMention?: (personId: string) => void
}

export function useMentionUpdates({
  defaultHousehold = null,
  defaultContact = null,
  defaultBusiness = null,
  defaultAssignee = null,
  defaultCollaborators = [],
  onRemovePersonMention,
}: UseMentionUpdatesProps = {}) {
  const [selectedHousehold, setSelectedHousehold] = useState<Household | null>(defaultHousehold)
  const [selectedContact, setSelectedContact] = useState<Contact | null>(defaultContact)
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(defaultBusiness)
  const [selectedAssignee, setSelectedAssignee] = useState<User | null>(defaultAssignee)
  const [selectedCollaborators, setSelectedCollaborators] = useState<MinimalUser[]>(defaultCollaborators || [])

  const handleMentionsUpdate = (updates: MentionUpdateData) => {
    if (updates.household) {
      setSelectedHousehold(updates.household)
      setSelectedContact(null)
      setSelectedBusiness(null)
    }

    if (updates.contact) {
      setSelectedContact(updates.contact)
      setSelectedHousehold(null)
      setSelectedBusiness(null)
    }

    if (updates.business) {
      setSelectedBusiness(updates.business)
      setSelectedHousehold(null)
      setSelectedContact(null)
    }

    if (updates.trust) {
      const hasExistingRelationship = selectedHousehold || selectedContact || selectedBusiness
      const mentionableRecord = updates.trust.mentionable_record

      if (mentionableRecord && mentionableRecord.type === "household") {
        if (!hasExistingRelationship) {
          setSelectedHousehold({
            id: mentionableRecord.id,
            name: mentionableRecord.label,
          } as Household)
          setSelectedContact(null)
          setSelectedBusiness(null)
        }
      } else if (updates.trust.household) {
        setSelectedHousehold(updates.trust.household as Household)
        setSelectedContact(null)
        setSelectedBusiness(null)
      }
    }
    if (updates.person) {
      const hasExistingRelationship = selectedHousehold || selectedContact || selectedBusiness
      const mentionableRecord = updates.person.mentionable_record

      if (mentionableRecord && !hasExistingRelationship) {
        if (mentionableRecord.type === "household") {
          setSelectedHousehold({
            id: mentionableRecord.id,
            name: mentionableRecord.label,
          } as Household)
          setSelectedContact(null)
          setSelectedBusiness(null)
        } else if (mentionableRecord.type === "contact") {
          setSelectedContact({
            id: mentionableRecord.id,
            person: {
              full_name: mentionableRecord.label,
            },
          } as Contact)
          setSelectedHousehold(null)
          setSelectedBusiness(null)
          onRemovePersonMention?.(updates.person.id)
        }
      }
    }

    if (updates.user_mention) {
      const { id, name } = updates.user_mention
      const user = { id, full_name: name } as MinimalUser

      if (!selectedCollaborators.some((collab) => collab.id === id)) {
        setSelectedCollaborators([...selectedCollaborators, user])
      }
    }

    if (updates.removed_user_mention) {
      const { id } = updates.removed_user_mention

      setSelectedCollaborators(
        selectedCollaborators.filter((collab) => {
          // Keep collaborator if they are the assignee
          if (selectedAssignee?.id === collab.id) {
            return true
          }
          return collab.id !== id
        })
      )
    }

    if (updates.removed_contact && selectedContact?.id === updates.removed_contact.id) {
      setSelectedContact(null)
    }

    if (updates.removed_household && selectedHousehold?.id === updates.removed_household.id) {
      setSelectedHousehold(null)
    }

    if (updates.removed_business && selectedBusiness?.id === updates.removed_business.id) {
      setSelectedBusiness(null)
    }

    if (updates.removed_trust) {
      const mentionableRecord = updates.removed_trust.mentionable_record

      if (
        mentionableRecord &&
        mentionableRecord.type === "household" &&
        selectedHousehold?.id === mentionableRecord.id
      ) {
        setSelectedHousehold(null)
      }
    }

    if (updates.removed_person) {
      const mentionableRecord = updates.removed_person.mentionable_record

      if (mentionableRecord) {
        if (mentionableRecord.type === "household" && selectedHousehold?.id === mentionableRecord.id) {
          setSelectedHousehold(null)
        } else if (mentionableRecord.type === "contact" && selectedContact?.id === mentionableRecord.id) {
          setSelectedContact(null)
        }
      }
    }
  }

  return {
    selectedHousehold,
    selectedContact,
    selectedBusiness,
    selectedAssignee,
    selectedCollaborators,
    setSelectedHousehold,
    setSelectedContact,
    setSelectedBusiness,
    setSelectedAssignee,
    setSelectedCollaborators,
    handleMentionsUpdate,
  }
}
